import { Component, Input, OnInit } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { MatDialogRef } from "@angular/material/dialog";
import * as moment from "moment/moment";
import { NgxSpinnerService } from "ngx-spinner";
import { throwError } from "rxjs";
import { catchError, tap } from "rxjs/operators";
import { Post } from "src/app/core/interfaces/post.interface";
import { Categories } from "src/app/core/models/categories.model";
import { Tags } from "src/app/core/models/tags.model";
import Swal, { SweetAlertType } from "sweetalert2";
import BaseComponent from "../../core/base-component";
import { StatusNewsFeed } from "../../core/enum/status-news-feed.enum";
import { ApiServiceNewsFeed } from "../../core/services/feed-news.service";

@Component({
  selector: "app-admin-dialog-feed-news",
  templateUrl: "admin-dialog-feed-news.component.html",
  styleUrls: ["./admin-dialog-feed-news.component.scss"]
})
export class AdminDialogFeedNewsComponent extends BaseComponent implements OnInit {
  categories: Array<Categories>;
  tags: Array<Tags> = [];
  tagName: string;
  categoryName: string;
  StatusNewsFeed = StatusNewsFeed;
  file: Blob;
  isEditCategory: boolean = false;
  isEditTag: boolean = false;
  selectedCategories: string[] = [];
  selectedTags: string[] = [];

  @Input() post;
  @Input() getAllPosts;

  constructor(
    private formBuilder: FormBuilder,
    private apiServiceNewsFeed: ApiServiceNewsFeed,
    private spinner: NgxSpinnerService,
    public dialogRef: MatDialogRef<AdminDialogFeedNewsComponent>
  ) {
    super();
  }

  form: FormGroup = this.createForm();

  optionsEditor = {
    height: 400,
    menubar: false,
    plugins: [
      "advlist autolink lists link image charmap print",
      "preview anchor searchreplace visualblocks code",
      "fullscreen insertdatetime media table paste",
      "help wordcount",
      "image media"
    ],
    content_style: `
           html::-webkit-scrollbar {
            width: 0.3em;
            height: 0.3em;
            background-color: rgba(232, 229, 229, 0.3);
          }

          html::-webkit-scrollbar-track {
            box-shadow: inset 0 0 1px rgba(232, 229, 229, 0.3);
            border-radius: 25px;
          }

          html::-webkit-scrollbar-thumb {
            background-color: #eb6625;
            outline: 1px solid #eb6625;
            border-radius: 5px;
          }
          body { color: black; }
        `,
    content_css: "admin-dialog-feed-news.component.scss",
    image_uploadtab: true,
    images_upload_handler: this.uploadImagesContent.bind(this),
    toolbar: `undo redo | formatselect | fontsizeselect forecolor backcolor | image media |tinydrive code imagetools | bold italic
            | alignleft aligncenter alignright alignjustify
            | bullist numlist outdent indent | help`
  };

  uploadImagesContent(blobInfo, success, failure, progress) {
    const formData = new FormData();
    formData.append("file", blobInfo.blob(), blobInfo.filename());

    const fileSize = blobInfo.blob().size;
    const fileType = blobInfo.blob().type;
    if (fileSize < 10485760) {
      if (fileType === "image/png" || fileType === "image/jpeg") {
        this.apiServiceNewsFeed
          .uploadImage(formData)
          .pipe(
            tap(res => {
              success(res.href);
            }),
            catchError(error => {
              failure("Error uploading image.");

              return throwError(error.message);
            })
          )
          .subscribe();
      } else {
        failure("Formato de arquivo não permitido");
      }
    } else {
      failure("Arquivo excede o tamanho de 10mb");
    }
  }

  handleUpload(event) {
    this.field("file").setValue(event.target.files[0]);
  }

  ngOnInit() {
    if (this.post) {
      this.changeFields();
    }
    this.createForm();
    this.getAllCategory();
    this.getAllTags();
  }

  createForm() {
    return this.formBuilder.group({
      author: [null],
      title: [null],
      content: [null],
      created_at: [null],
      file: [null],
      status: [StatusNewsFeed.PUBLISHED]
    });
  }

  setEditCategory() {
    this.isEditCategory = true;
  }

  setEditTag() {
    this.isEditTag = true;
  }

  changeFields() {
    this.field("author").setValue(this.post.author);
    this.field("title").setValue(this.post.title);
    this.field("content").setValue(this.post.content);
    this.field("created_at").setValue(moment(this.post.created_at).format("YYYY-MM-DD"));
    if (this.post.status !== StatusNewsFeed.ARCHIVED) {
      this.field("status").setValue(this.post.status);
    }

    this.selectedCategories = this.post.categories.map(item => item.uuid);
    this.selectedTags = this.post.tags.map(item => item.uuid);
  }

  alert(title: string, icon: SweetAlertType) {
    Swal.fire({
      type: icon,
      title,
      showConfirmButton: false,
      timer: 1500
    });
  }

  getAllCategory() {
    this.apiServiceNewsFeed.getAllCategory("ACTIVE").subscribe({
      next: res => {
        if (res.message == "operation with success") {
          this.categories = [];
        } else {
          this.categories = res;
        }
      },
      error: error => {
        throw Error(error.message);
      }
    });
  }

  getAllTags() {
    this.apiServiceNewsFeed.getAllTags("ACTIVE").subscribe({
      next: res => {
        if (res.message == "operation with success") {
          this.tags = [];
        } else {
          this.tags = res;
        }
      },
      error: error => {
        throw Error(error.message);
      }
    });
  }

  archivedCategory(uuid: string) {
    this.apiServiceNewsFeed.archivedCategory(uuid).subscribe({
      next: () => {
        this.categories = this.categories.filter(item => item.uuid !== uuid);
      },
      error: error => {
        throw Error(error.message);
      }
    });
  }

  archivedTag(uuid: string) {
    this.apiServiceNewsFeed.archivedTag(uuid).subscribe({
      next: () => {
        this.tags = this.tags.filter(item => item.uuid !== uuid);
      },
      error: error => {
        throw Error(error.message);
      }
    });
  }

  addCategory() {
    const name = this.categoryName;

    if (!name) {
      this.isEditCategory = false;
      return;
    }

    this.apiServiceNewsFeed.createCategory(name).subscribe({
      next: res => {
        this.categories.push({ uuid: res.uuid, name: res.name, isActive: false });
        this.alert("Categoria criada com sucesso", "success");
        this.categoryName = "";
        this.isEditCategory = false;
      },
      error: error => {
        this.alert("Ops!! não foi possivel criar a categoria", "warning");
        this.isEditCategory = false;
        throw Error(error.message);
      }
    });
  }

  addTag() {
    const tag = this.tagName;

    if (!tag) {
      this.isEditTag = false;
      return;
    }

    this.apiServiceNewsFeed.createTag(tag).subscribe({
      next: res => {
        this.tags.push({ uuid: res.uuid, name: res.name, isActive: false });
        this.tagName = "";
        this.isEditTag = false;
      },
      error: error => {
        this.alert("Ops!! não foi possivel criar a tag", "warning");
        this.isEditTag = false;
        throw Error(error.message);
      }
    });
  }

  save() {
    this.spinner.show();
    const formData: FormData = new FormData();
    Object.keys(this.form.value).forEach(value => formData.append(value, this.form.value[value]));
    this.selectedCategories.forEach(item => formData.append("categories[]", item));
    this.selectedTags.forEach(item => formData.append("tags[]", item));

    this.apiServiceNewsFeed.createPost(formData).subscribe({
      next: () => {
        this.getAllPosts();
        this.dialogRef.close();
        this.spinner.hide();
        this.alert("Post salvo com sucesso", "success");
      },
      error: error => {
        this.spinner.hide();
        this.alert("Ops!! não foi possivel salvar o post", "error");
        throw Error(error.message);
      }
    });
  }

  updatePost() {
    this.spinner.show();
    const formData: FormData = new FormData();
    Object.keys(this.form.value).forEach(value => formData.append(value, this.form.value[value]));
    this.selectedCategories.forEach(item => formData.append("categories[]", item));
    this.selectedTags.forEach(item => formData.append("tags[]", item));

    this.apiServiceNewsFeed.updatePost(this.post.uuid, formData).subscribe({
      next: res => {
        this.getAllPosts();
        this.post = res;
        this.dialogRef.close();
        this.spinner.hide();
        this.alert("Post salvo com sucesso", "success");
      },
      error: error => {
        this.spinner.hide();
        this.alert("Ops!! não foi possivel salvar o post", "error");
        throw Error(error.message);
      }
    });
  }

  onChangeCategory(uuid: string) {
    const categories = this.selectedCategories;
    if (categories.filter(i => i === uuid).length) {
      this.selectedCategories = categories.filter(i => i !== uuid);
      return;
    }

    this.selectedCategories.push(uuid);
  }

  onChangeTag(uuid: string) {
    const categories = this.selectedTags;
    if (categories.filter(i => i === uuid).length) {
      this.selectedTags = categories.filter(i => i !== uuid);
      return;
    }

    this.selectedTags.push(uuid);
  }

  checkStatus(status) {
    if (this.post && this.post.status === status) {
      return true;
    }

    return false;
  }

  checkField() {
    if (
      this.field("author").value &&
      this.field("title").value &&
      this.field("content").value &&
      this.field("created_at").value &&
      this.selectedCategories.length &&
      this.selectedTags.length
    ) {
      return false;
    }

    return true;
  }
}
